import { attempt } from "@omni/utils/function.utils";
import * as LocalStorage from "@omni/utils/localStorage.utils";
import aa from "search-insights";
import { v4 as uuidv4 } from "uuid";

import * as ConsentService from "@/services/consent/consent.service";
import { getUserInfo } from "@/services/tracking/trackers/utils";
import * as TrackingService from "@/services/tracking/tracking.service";
import { StorageKeys } from "@/utils/enums";

import * as AlgoliaUserService from "./algoliaUser.service";

type AlgoliaRecord = {
    queryID: string;
    sku: string;
    timestamp: number;
    position: number;
};
type AlgoliaQueryRecord = Array<AlgoliaRecord>;

const setGAUserToken = () => {
    const userToken =
        TrackingService.getGoogleAnalyticsUserIdFromBrowserCookie("_ga");

    if (userToken) {
        aa("setUserToken", userToken);
    }
};

const setAnonymousUserToken = () => {
    const anonymousUserToken = `anonymous-${uuidv4()}`;
    aa("setUserToken", anonymousUserToken);
};

const setUserToken = async () => {
    if (await ConsentService.isFunctionalCookiesEnabled()) {
        setGAUserToken();
    } else {
        setAnonymousUserToken();
    }
};

export const getAlgoliaQueryTrackRecord = () => {
    return attempt(
        () =>
            JSON.parse(
                LocalStorage.getItem(StorageKeys.AlgoliaSearchQueryTracking) ||
                    ""
            ),
        []
    ) as AlgoliaQueryRecord;
};

export const init = async () => {
    const { appId, searchApiKey } =
        await AlgoliaUserService.initAlgoliaParameters();

    aa("init", {
        partial: true,
        apiKey: searchApiKey,
        appId: appId
    });

    aa("onAuthenticatedUserTokenChange", (newToken) => {
        if (newToken) {
            LocalStorage.setItem(
                StorageKeys.AlgoliaAuthenticatedUserToken,
                newToken.toString()
            );
        } else {
            LocalStorage.removeItem(StorageKeys.AlgoliaAuthenticatedUserToken);
        }
    });

    aa("onUserTokenChange", (newToken) => {
        if (newToken) {
            LocalStorage.setItem(
                StorageKeys.AlgoliaUserToken,
                newToken.toString()
            );
        } else {
            LocalStorage.removeItem(StorageKeys.AlgoliaUserToken);
        }
    });

    ConsentService.handleOnConsentChanged(() => {
        setUserToken();
    });

    setUserToken();
};

export const authenticate = () => {
    const clientID = getUserInfo().clientID;
    if (clientID) {
        aa("setAuthenticatedUserToken", clientID);
    }
};

export const logout = () => {
    aa("setAuthenticatedUserToken", undefined);
};

export const trackQuery = (queryID: string, sku: string, position: number) => {
    const algoliaQueryRecords = getAlgoliaQueryTrackRecord();
    algoliaQueryRecords.push({
        queryID,
        sku,
        timestamp: Date.now(),
        position
    });

    LocalStorage.setItem(
        StorageKeys.AlgoliaSearchQueryTracking,
        JSON.stringify(algoliaQueryRecords)
    );
};

export const getLastQueryTrackRecordFor = (sku: string) => {
    const algoliaQueryRecords = getAlgoliaQueryTrackRecord();
    return algoliaQueryRecords
        .sort((a, b) => b.timestamp - a.timestamp)
        .find((r) => r.sku === sku);
};

export const removeQueryTrackRecordFor = (sku: string) => {
    const algoliaQueryRecords = getAlgoliaQueryTrackRecord();
    const newRecords = algoliaQueryRecords.filter((r) => r.sku !== sku);

    LocalStorage.setItem(
        StorageKeys.AlgoliaSearchQueryTracking,
        JSON.stringify(newRecords)
    );
};
